'use client'

import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import type { MuxCSSProperties } from '@mux/mux-player-react'
import { afterLoadWhenIdle } from '@/lib/schedule'

/**
 * ~90KB gz of player + hls.js. This used to be a static import, which put it in
 * the homepage's initial chunk graph — and, far worse, meant the poster frame
 * lived in the player's shadow DOM and could not be requested until that chunk
 * had downloaded, parsed, executed, and upgraded the custom element. The LCP
 * image was invisible to the preload scanner. Now the poster is plain SSR'd
 * markup and the player arrives afterwards.
 */
const MuxPlayer = dynamic(() => import('@mux/mux-player-react'), { ssr: false })

const POSTER_WIDTHS = [480, 640, 828, 1080, 1280, 1600]

/**
 * No `fit_mode` — keep the source aspect ratio so CSS `object-fit: cover` plus
 * `object-position` crops the poster exactly the way the player crops the
 * <video>. (Deliberately not reusing `MuxFacadePlayer`'s `muxPoster()`: that one
 * smartcrops to a fixed 16:9, which reframes this ~1390x1080 source and makes
 * the poster→video swap visibly jump.)
 */
function posterUrl(playbackId: string, width: number) {
  return `https://image.mux.com/${playbackId}/thumbnail.webp?width=${width}`
}

const OBJECT_POSITION = '54% 48%'

type Props = {
  playbackId: string
  /**
   * Viewport floor for loading the video at all. 0 plays everywhere; set to
   * e.g. 768 to leave phones on the poster frame and skip hls.js + continuous
   * decode on the slowest devices.
   */
  minWidth?: number
}

export default function HomepageHeroVideo({ playbackId, minWidth = 0 }: Props) {
  const [mountPlayer, setMountPlayer] = useState(false)
  const [videoReady, setVideoReady] = useState(false)

  const poster = posterUrl(playbackId, 1280)

  useEffect(() => {
    // An autoplaying looping background is exactly the motion WCAG 2.2.2 is
    // about, and Data Saver users have asked not to be served video.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    if (minWidth > 0 && !window.matchMedia(`(min-width: ${minWidth}px)`).matches) return
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    if (connection?.saveData) return

    return afterLoadWhenIdle(() => setMountPlayer(true))
  }, [minWidth])

  return (
    // bg-bg backs the poster so the hero is never a transparent gap before
    // the image paints, and never a broken-image glyph if Mux is unreachable.
    <div className="absolute inset-0 overflow-hidden bg-bg">
      {/*
        The LCP element. Server-rendered, so the preload scanner finds it in the
        first bytes of HTML instead of behind the player bundle. Never unmounted
        — it also covers the cases where the video never starts: reduced motion,
        Data Saver, and iOS Low Power Mode blocking muted autoplay.
      */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={poster}
        srcSet={POSTER_WIDTHS.map((w) => `${posterUrl(playbackId, w)} ${w}w`).join(', ')}
        sizes="100vw"
        alt=""
        aria-hidden="true"
        width={1390}
        height={1080}
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: OBJECT_POSITION }}
      />

      {mountPlayer && (
        <MuxPlayer
          playbackId={playbackId}
          streamType="on-demand"
          autoPlay="muted"
          loop
          muted
          playsInline
          // Uncapped, Mux served 7.1MB across five HLS segments to a 412px
          // viewport. This is a decorative loop painting at roughly 400px tall —
          // it never needs a 1080p+ rendition. 720p is the lowest cap Mux offers.
          maxResolution="720p"
          // Decorative background loop — playback analytics tell us nothing, and
          // Mux Data costs a connection to inferred.litix.io during load.
          disableTracking
          disableCookies
          // Byte-identical to the <img> above: cache hit, no second request, and
          // no flash if the player paints its own poster first.
          poster={poster}
          onPlaying={() => setVideoReady(true)}
          className="absolute inset-0 h-full w-full transition-opacity duration-700 motion-reduce:transition-none"
          style={
            {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: OBJECT_POSITION,
              // Held invisible until the first frame is actually playing, so the
              // handoff from poster to video is a crossfade rather than a flash.
              opacity: videoReady ? 1 : 0,
              '--controls': 'none',
              '--media-object-fit': 'cover',
              '--media-object-position': OBJECT_POSITION,
            } as MuxCSSProperties
          }
        />
      )}

      {/*
        Legibility scrim. The footage is busy and the headline sits directly on
        top of it, so a flat wash knocks the video back without washing it out.
        A separate layer rather than `opacity` on the media itself: the player
        already animates its own opacity for the poster handoff, and dimming
        both would briefly stack them into a bright flash mid-crossfade.
        Sits above the video, below the headline — which is a later sibling in
        the section and carries `relative`.
      */}
      <div aria-hidden="true" className="absolute inset-0 bg-bg/55" />
    </div>
  )
}
